import { useMemo, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import { type PersonnelDetailsDto, type PersonnelListQuery } from '@ctms/contracts';
import { Building2, Search, UserRound } from 'lucide-react';

import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { ContactCardDialog, type ContactEditor } from '../contact-card-dialog/contact-card-dialog.js';
import { trialsApi } from '../../trials/trials.api.js';
import { trialQueryKeys } from '../../trials/trials.query-keys.js';

export interface ContactsCatalogProps {
  canRead: boolean;
  canWrite: boolean;
}

const columnHelper = createAppColumnHelper<PersonnelDetailsDto>();

const LIST_LIMIT = 25;

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function displayValue(value: string | null): string {
  return value == null || value.length === 0 ? '—' : value;
}

export function ContactsCatalog({ canRead, canWrite }: ContactsCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [internalOnly, setInternalOnly] = useState(false);
  const [editor, setEditor] = useState<ContactEditor | null>(null);

  const listFilters: PersonnelListQuery = useMemo(() => {
    const filters: PersonnelListQuery = { page, limit: LIST_LIMIT };
    const trimmed = search.trim();
    if (trimmed.length > 0) {
      filters.search = trimmed;
    }
    if (internalOnly) {
      filters.internalPersonnel = true;
    }
    return filters;
  }, [page, search, internalOnly]);

  const contactsQuery = useQuery({
    queryKey: trialQueryKeys.personnelCards(listFilters),
    queryFn: () => trialsApi.listPersonnel(listFilters),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'type',
          header: 'Typ',
          cell: (info) => {
            const card = info.row.original;
            return card.internalPersonnel ? (
              <span role="img" aria-label="Pracownik ośrodka" title="Pracownik ośrodka">
                <Building2 className="h-4 w-4 text-cyan-300" />
              </span>
            ) : (
              <span role="img" aria-label="Osoba z zewnątrz" title="Osoba z zewnątrz">
                <UserRound className="h-4 w-4 text-slate-400" />
              </span>
            );
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Nazwisko',
          cell: (info) => displayValue(info.getValue()),
        }),
        columnHelper.accessor('firstName', {
          header: 'Imię',
          cell: (info) => displayValue(info.getValue()),
        }),
        columnHelper.accessor('phoneMobile', {
          header: 'Telefon',
          cell: (info) => displayValue(info.getValue()),
        }),
        columnHelper.accessor('email1', {
          header: 'E-mail',
          cell: (info) => displayValue(info.getValue()),
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Działania',
          cell: (info) => {
            const card = info.row.original;
            const label = `${displayValue(card.firstName)} ${displayValue(card.lastName)}`.trim();
            return (
              <button
                type="button"
                aria-label={`Otwórz ${label}`}
                onClick={() => setEditor({ mode: 'edit', id: card.id })}
                className="rounded border border-cyan-800/60 px-2 py-1 text-xs text-cyan-300"
              >
                Otwórz
              </button>
            );
          },
        }),
      ]),
    [setEditor],
  );

  const items = contactsQuery.data?.items ?? [];
  const table = useAppTable({ data: items, columns });

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">Brak dostępu do kontaktów</h2>
        <p className="mt-2 text-xs text-slate-400">Nie masz uprawnień do przeglądania kartoteki kontaktów.</p>
      </section>
    );
  }

  const isLoading = contactsQuery.isLoading;
  const isError = contactsQuery.isError;
  const isEmptyCatalog = !isLoading && !isError && items.length === 0 && search.trim().length === 0;

  return (
    <div className="space-y-4">
      <div className="border-b border-slate-800 pb-4">
        <h2 className="text-lg font-semibold text-slate-100">Kontakty wspólne dla badań</h2>
        <p className="mt-1 text-xs text-slate-400">
          Jedna karta osoby służy wielu badaniom. Konta logowania do programu są w zakładce „Konta i uprawnienia”.
        </p>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-900/80 p-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute top-2 left-2.5 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            aria-label="Szukaj po nazwisku, imieniu, e-mailu lub telefonie"
            placeholder="Szukaj po nazwisku, imieniu, e-mailu lub telefonie…"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pr-3 pl-8 text-xs text-slate-100"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={internalOnly}
            onChange={(event) => {
              setInternalOnly(event.target.checked);
              setPage(1);
            }}
          />
          Tylko pracownicy ośrodka
        </label>
        {canWrite ? (
          <button
            type="button"
            onClick={() => setEditor({ mode: 'create' })}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
          >
            Nowa karta kontaktowa
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-400">Ładowanie kartoteki</p>
      ) : isError ? (
        <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-6">
          <h2 className="text-sm font-semibold text-rose-200">Nie udało się wczytać kontaktów</h2>
          <p className="mt-2 text-xs text-rose-200/80">
            Spróbuj ponownie. Jeżeli problem się powtarza, zgłoś go administratorowi ośrodka.
          </p>
          <button
            type="button"
            onClick={() => {
              void contactsQuery.refetch();
            }}
            className="mt-3 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
          >
            Spróbuj ponownie
          </button>
        </section>
      ) : isEmptyCatalog ? (
        <section className="rounded-lg border border-slate-800 bg-slate-900/60 p-8 text-center">
          <h2 className="text-base font-semibold text-slate-100">Brak kart kontaktowych</h2>
          <p className="mt-2 text-xs text-slate-400">
            Dodaj osobę z imieniem i nazwiskiem, zanim będzie można przypisać ją do zespołu badania.
          </p>
        </section>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-left text-xs">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} scope="col" className="px-3 py-2 text-slate-400">
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-8 text-center text-slate-500">
                    Brak kart spełniających kryteria wyszukiwania.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/80">
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ContactCardDialog
        editor={editor}
        canWrite={canWrite}
        onClose={() => setEditor(null)}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['personnel'] });
        }}
      />

      {!isLoading && !isError && (contactsQuery.data?.total ?? 0) > LIST_LIMIT && (
        <div className="flex justify-end gap-2 text-xs">
          <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            Poprzednia
          </button>
          <button
            type="button"
            disabled={!contactsQuery.data?.hasNext}
            onClick={() => setPage((current) => current + 1)}
          >
            Następna
          </button>
        </div>
      )}
    </div>
  );
}
